import React, { useState, useEffect, useCallback } from 'react';
import { Search, Upload, FileSpreadsheet, CheckCircle, AlertCircle, ArrowRight, ArrowLeft, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import api from '../api';

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Upload wizard state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadStep, setUploadStep] = useState(1);
  const [uploadResult, setUploadResult] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [processResult, setProcessResult] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const loadCatalog = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (brandFilter) params.set('brand', brandFilter);
      params.set('page', page);
      params.set('limit', 30);
      const result = await api.catalog(params.toString());
      setProducts(result.products || []);
      setTotal(result.total || 0);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => {
    api.brands().then(b => setBrands(b || [])).catch(() => {});
  }, []);

  useEffect(() => { loadCatalog(); }, [page, search, brandFilter]);

  // Upload handlers
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setUploadLoading(true);
    setUploadError('');
    try {
      const result = await api.uploadCatalog(acceptedFiles[0]);
      setUploadResult(result);
      // Auto-detect column mapping
      const cols = result.columns || [];
      const mapping = {};
      for (const col of cols) {
        const lower = col.toLowerCase();
        if (['producto', 'nombre', 'descripcion', 'product', 'item', 'articulo'].some(k => lower.includes(k))) mapping.nombre = col;
        if (['sku', 'clave', 'codigo', 'code'].some(k => lower.includes(k))) mapping.sku = col;
        if (['marca', 'brand'].some(k => lower.includes(k))) mapping.marca = col;
        if (['linea', 'línea', 'line', 'tipo'].some(k => lower.includes(k))) mapping.linea = col;
        if (['viscosidad', 'visc', 'sae'].some(k => lower.includes(k))) mapping.viscosidad = col;
        if (['presentacion', 'presentación', 'envase', 'tamano'].some(k => lower.includes(k))) mapping.presentacion = col;
        if (['min', 'minimo', 'mínimo', 'min_stock'].some(k => lower.includes(k))) mapping.min_stock = col;
        if (['max', 'maximo', 'máximo', 'max_stock'].some(k => lower.includes(k))) mapping.max_stock = col;
        if (['abc', 'clasificacion', 'clase'].some(k => lower.includes(k))) mapping.abc_class = col;
        if (['lead', 'entrega', 'dias'].some(k => lower.includes(k))) mapping.lead_time = col;
      }
      setColumnMapping(mapping);
      setUploadStep(2);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploadLoading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
  });

  const handleProcessCatalog = async () => {
    if (!uploadResult?.staging_id) return;
    setUploadLoading(true);
    setUploadError('');
    try {
      const result = await api.processCatalogUpload(uploadResult.staging_id, { column_mapping: columnMapping });
      setProcessResult(result);
      setUploadStep(3);
      // Reload catalog
      loadCatalog();
      api.brands().then(b => setBrands(b || [])).catch(() => {});
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploadLoading(false);
    }
  };

  const resetUpload = () => {
    setShowUpload(false);
    setUploadStep(1);
    setUploadResult(null);
    setColumnMapping({});
    setProcessResult(null);
    setUploadError('');
  };

  const MAPPING_FIELDS = [
    { key: 'nombre', label: 'Nombre / Descripción del producto', required: true },
    { key: 'sku', label: 'SKU / Clave' },
    { key: 'marca', label: 'Marca' },
    { key: 'linea', label: 'Línea / Tipo' },
    { key: 'viscosidad', label: 'Viscosidad / SAE' },
    { key: 'presentacion', label: 'Presentación / Envase' },
    { key: 'min_stock', label: 'Stock Mínimo' },
    { key: 'max_stock', label: 'Stock Máximo' },
    { key: 'abc_class', label: 'Clasificación ABC' },
    { key: 'lead_time', label: 'Lead Time (días)' },
  ];

  // Upload wizard modal
  if (showUpload) {
    return (
      <div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'20px'}}>
          <h2 style={{fontSize:'1.1rem',fontWeight:600}}>Cargar Catálogo Maestro</h2>
          <button className="btn btn-secondary" onClick={resetUpload}><X size={14} /> Cerrar</button>
        </div>
        
        <p className="section-description">
          Sube un archivo Excel o CSV con tu catálogo de productos. Este será tu catálogo maestro contra el cual se homologarán las facturas de proveedores.
        </p>

        {uploadError && (
          <div style={{ background: 'hsl(0 72% 48% / 0.08)', color: 'hsl(0 72% 40%)', padding: '12px 16px', borderRadius: '8px', fontSize: '0.82rem', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertCircle size={16} /> {uploadError}
          </div>
        )}

        {/* Step 1: Upload File */}
        {uploadStep === 1 && (
          <div {...getRootProps()} className={`upload-zone ${isDragActive ? 'active' : ''}`}>
            <input {...getInputProps()} />
            <Upload size={40} />
            <h3 style={{marginBottom:'4px'}}>Arrastra tu archivo de catálogo aquí</h3>
            <p style={{color:'hsl(var(--muted-foreground))',fontSize:'0.82rem'}}>Excel (.xlsx, .xls) o CSV con la lista completa de tus productos</p>
            {uploadLoading && <div style={{marginTop:'12px'}}><div className="spinner" style={{margin:'0 auto'}} /></div>}
          </div>
        )}

        {/* Step 2: Preview + Column Mapping */}
        {uploadStep === 2 && uploadResult && (
          <div>
            <div className="section-card">
              <h3><FileSpreadsheet size={16} style={{display:'inline',marginRight:'6px'}} />{uploadResult.filename}</h3>
              <p style={{fontSize:'0.8rem',color:'hsl(var(--muted-foreground))',marginTop:'2px'}}>{uploadResult.rows} productos | {uploadResult.columns?.length} columnas</p>
              
              <div style={{overflowX:'auto',marginTop:'12px',maxHeight:'200px',overflowY:'auto'}}>
                <table className="data-table">
                  <thead>
                    <tr>{uploadResult.columns?.map(c => <th key={c}>{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {uploadResult.preview?.slice(0, 5).map((row, i) => (
                      <tr key={i}>{uploadResult.columns?.map(c => <td key={c}>{row[c] != null ? String(row[c]) : '-'}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="section-card" style={{marginTop:'16px'}}>
              <h3>Mapeo de columnas</h3>
              <p style={{fontSize:'0.82rem',color:'hsl(var(--muted-foreground))',marginBottom:'16px'}}>
                Indica qué columna de tu archivo corresponde a cada campo. Solo "Nombre" es obligatorio.
              </p>
              {MAPPING_FIELDS.map(field => (
                <div key={field.key} style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'10px'}}>
                  <label style={{width:'200px',fontSize:'0.82rem',fontWeight:field.required ? 600 : 400}}>
                    {field.label} {field.required && <span style={{color:'hsl(0,72%,48%)'}}>*</span>}
                  </label>
                  <select
                    className="filter-select" style={{flex:1}}
                    value={columnMapping[field.key] || ''}
                    onChange={e => setColumnMapping({...columnMapping, [field.key]: e.target.value})}
                  >
                    <option value="">-- No mapear --</option>
                    {uploadResult.columns?.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <div style={{display:'flex',gap:'8px',marginTop:'16px'}}>
              <button className="btn btn-secondary" onClick={() => setUploadStep(1)}>
                <ArrowLeft size={14} /> Volver
              </button>
              <button className="btn btn-primary" onClick={handleProcessCatalog} disabled={uploadLoading || !columnMapping.nombre}>
                {uploadLoading ? 'Procesando...' : 'Importar catálogo'}
                {!uploadLoading && <CheckCircle size={14} />}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Results */}
        {uploadStep === 3 && processResult && (
          <div className="section-card" style={{textAlign:'center'}}>
            <CheckCircle size={48} style={{color:'hsl(152,63%,33%)',marginBottom:'12px'}} />
            <h3>Catálogo maestro actualizado</h3>
            <div style={{display:'flex',gap:'24px',justifyContent:'center',marginTop:'16px'}}>
              <div>
                <div style={{fontSize:'1.5rem',fontWeight:700,fontFamily:'Space Grotesk',color:'hsl(152,63%,33%)'}}>{processResult.created}</div>
                <div style={{fontSize:'0.75rem',color:'hsl(var(--muted-foreground))'}}>Productos nuevos</div>
              </div>
              <div>
                <div style={{fontSize:'1.5rem',fontWeight:700,fontFamily:'Space Grotesk',color:'hsl(199,84%,38%)'}}>{processResult.updated}</div>
                <div style={{fontSize:'0.75rem',color:'hsl(var(--muted-foreground))'}}>Actualizados</div>
              </div>
              <div>
                <div style={{fontSize:'1.5rem',fontWeight:700,fontFamily:'Space Grotesk',color:'hsl(0,72%,48%)'}}>{processResult.errors_count}</div>
                <div style={{fontSize:'0.75rem',color:'hsl(var(--muted-foreground))'}}>Errores</div>
              </div>
            </div>
            <button className="btn btn-primary" onClick={resetUpload} style={{marginTop:'20px'}}>
              Ver catálogo actualizado
            </button>
          </div>
        )}
      </div>
    );
  }

  // Main catalog view
  return (
    <div>
      <div className="data-table-container">
        <div className="data-table-header">
          <h3>{total} productos en catálogo</h3>
          <div className="data-table-actions">
            <div className="search-input">
              <Search size={15} />
              <input placeholder="Buscar producto..." data-testid="catalog-search-input" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="filter-select" data-testid="catalog-filter-button" value={brandFilter} onChange={e => { setBrandFilter(e.target.value); setPage(1); }}>
              <option value="">Todas las marcas</option>
              {brands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
            <button className="btn btn-primary" onClick={() => setShowUpload(true)} data-testid="catalog-upload-button">
              <Upload size={14} /> Cargar catálogo
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <Upload size={48} />
            <h3>Sin productos en el catálogo</h3>
            <p>Sube tu catálogo maestro de productos para comenzar.</p>
            <button className="btn btn-primary" onClick={() => setShowUpload(true)} style={{marginTop:'8px'}}>
              <Upload size={14} /> Cargar catálogo maestro
            </button>
          </div>
        ) : (
          <>
            <table className="data-table" data-testid="catalog-products-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Producto</th>
                  <th>Marca</th>
                  <th>Línea</th>
                  <th>Viscosidad</th>
                  <th>Presentación</th>
                  <th>ABC</th>
                  <th className="right">Lead Time</th>
                  <th className="right">Min</th>
                  <th className="right">Max</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.product_id}>
                    <td className="mono" style={{fontSize:'0.75rem'}}>{p.sku_internal}</td>
                    <td style={{fontWeight:500,maxWidth:'250px'}}>
                      <div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.canonical_name}</div>
                    </td>
                    <td>{p.brand_name}</td>
                    <td style={{fontSize:'0.78rem'}}>{p.line}</td>
                    <td className="mono">{p.viscosity}</td>
                    <td>{p.presentation_label}</td>
                    <td><span className={`abc-badge ${p.abc_class}`}>{p.abc_class}</span></td>
                    <td className="right mono">{p.default_lead_time_days}d</td>
                    <td className="right mono">{p.min_stock_units}</td>
                    <td className="right mono">{p.max_stock_units}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>&lt;</button>
              <span style={{fontSize:'0.78rem',padding:'0 8px'}}>Página {page} de {Math.ceil(total / 30) || 1}</span>
              <button disabled={page >= Math.ceil(total / 30)} onClick={() => setPage(p => p + 1)}>&gt;</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
